import { FC, useState } from 'react';
import { Button } from 'reactstrap';

import { AIDisclaimer } from '@g17eco/molecules/ai-disclaimer';
import { BannerAlert } from '@g17eco/molecules/alert/BannerAlert';
import { BasicAlert } from '@g17eco/molecules/alert/BasicAlert';
import { StatusBadge } from '@g17eco/molecules/badge/StatusBadge';
import { Breadcrumbs } from '@g17eco/molecules/breadcrumbs/Breadcrumbs';
import ButtonWithLoader from '@g17eco/molecules/button/ButtonWithLoader';
import IconButton from '@g17eco/molecules/button/IconButton';
import { SubmitButton } from '@g17eco/molecules/button/SubmitButton';
import { CollapseButton, CollapseContent, CollapsePanel } from '@g17eco/molecules/collapse-panel';
import { ConfirmationModal } from '@g17eco/molecules/confirm-modal/ConfirmationModal';
import { DatePicker } from '@g17eco/molecules/date-picker';
import { DownloadButton } from '@g17eco/molecules/button/DownloadButton';
import { DownloadCard } from '@g17eco/molecules/download-card/DownloadCard';
import Dashboard, { DashboardSection, DashboardRow, DashboardColumn } from '@g17eco/molecules/dashboard';
import { FeatureIntro } from '@g17eco/molecules/feature-intro';
import { ExtraFeature, FeatureStability } from '@g17eco/molecules/feature-stability';
import { FilterToggle } from '@g17eco/molecules/filter-toggle';
import { CheckboxInput } from '@g17eco/molecules/form/CheckboxInput';
import { ColourPicker } from '@g17eco/molecules/form/ColourPicker';
import { RadioInput } from '@g17eco/molecules/form/RadioInput';
import { InfoIcon } from '@g17eco/molecules/info-icon';
import { LoadingPlaceholder } from '@g17eco/molecules/loader-container';
import { G17Lightbox, LightboxSlide, CLOSED_PHOTO_INDEX } from '@g17eco/molecules/lightbox';
import { NoData } from '@g17eco/molecules/no-data';
import { ExpandableParagraph } from '@g17eco/molecules/paragraph/ExpandableParagraph';
import { PermissionDenied } from '@g17eco/molecules/permission-denied';
import { PopoverButton } from '@g17eco/molecules/popover/PopoverButton';
import { QueryWrapper } from '@g17eco/molecules/query/QueryWrapper';
import { QueryError } from '@g17eco/molecules/query/QueryError';
import { RadioGroup } from '@g17eco/molecules/radio-group';
import SDGIcon from '@g17eco/molecules/sdg-icon/sdg-icon';
import { DebouncedSearchBox } from '@g17eco/molecules/search/DebouncedSearchBox';
import { SearchBox } from '@g17eco/molecules/search/SearchBox';
import SettingsSidebar, { SettingsSection, SearchBox as SettingsSearchBox } from '@g17eco/molecules/settings-sidebar';
import { Sidebar } from '@g17eco/molecules/sidebar';
import { SelectFactory, SelectTypes, Option } from '@g17eco/molecules/select/SelectFactory';
import { SelectedCountLabel } from '@g17eco/molecules/selected-count-label';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { StaffIcon } from '@g17eco/molecules/staff-icon';
import { SwitchGroup } from '@g17eco/molecules/switch-group';
import { Toggle } from '@g17eco/molecules/toggle';
import { WorkInProgress } from '@g17eco/molecules/work-in-progress';
import { WorkgroupIcon } from '@g17eco/molecules/workgroup/WorkgroupIcon';
import { ShowcaseItem } from '@routes/theme-showcase/components/ShowcaseSection';
import { BadgesTruncate } from '@g17eco/molecules/badge/BadgesTruncate';
import { ColumnDef, Table } from '@g17eco/molecules/table/Table';
import { TimeRangeSelector } from '@g17eco/molecules/time-range-selector/TimeRangeSelector';
import { generateToast } from '@g17eco/molecules/toasts';
import { TableDraggableRows } from '@g17eco/molecules/table-draggable-rows';
import TrackingList from '@g17eco/molecules/tracking-list/list';
import TrackingListRow from '@g17eco/molecules/tracking-list/row';
import TrackingListCol from '@g17eco/molecules/tracking-list/col';
import { FloatingToolbar } from '@g17eco/molecules/floating-toolbar';
import { Footer } from '@g17eco/molecules/footer';
import { Menu } from '@g17eco/molecules/menu/Menu';
import { DateRangeType } from '@g17eco/types/common';
import styleVariables from '@css/variables.module.scss';

export const MoleculesShowcase: FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [toggleOn, setToggleOn] = useState(false);
  const [radioValue, setRadioValue] = useState('1');
  const [showBannerAlert, setShowBannerAlert] = useState(false);

  const [dateRange, setDateRange] = useState<DateRangeType>({ startDate: new Date(), endDate: new Date() });
  const [timeRange, setTimeRange] = useState<string | number>('yearly');

  // New Molecules State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [datePickerDate, setDatePickerDate] = useState<Date | undefined>(new Date());
  const [lightboxIndex, setLightboxIndex] = useState(CLOSED_PHOTO_INDEX);

  const lightboxSlides: LightboxSlide[] = [
    {
      src: 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22800%22%20height%3D%22600%22%3E%3Crect%20fill%3D%22%23cccccc%22%20width%3D%22800%22%20height%3D%22600%22%2F%3E%3Ctext%20fill%3D%22%23333333%22%20font-family%3D%22sans-serif%22%20font-size%3D%2260%22%20dy%3D%2210.5%22%20font-weight%3D%22bold%22%20x%3D%2250%25%22%20y%3D%2250%25%22%20text-anchor%3D%22middle%22%3ESlide%201%3C%2Ftext%3E%3C%2Fsvg%3E',
      title: 'Slide 1',
      description: <div>Description 1</div>,
    },
    {
      src: 'data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22800%22%20height%3D%22600%22%3E%3Crect%20fill%3D%22%23cccccc%22%20width%3D%22800%22%20height%3D%22600%22%2F%3E%3Ctext%20fill%3D%22%23333333%22%20font-family%3D%22sans-serif%22%20font-size%3D%2260%22%20dy%3D%2210.5%22%20font-weight%3D%22bold%22%20x%3D%2250%25%22%20y%3D%2250%25%22%20text-anchor%3D%22middle%22%3ESlide%202%3C%2Ftext%3E%3C%2Fsvg%3E',
      title: 'Slide 2',
    },
  ];

  // Form Controls State
  const [checkboxState, setCheckboxState] = useState<{ [key: string]: boolean }>({ chk1: true });
  const [switchGroupState, setSwitchGroupState] = useState(false);

  // Selection State
  const [singleSelectValue, setSingleSelectValue] = useState<Option | null>({
    label: 'Option A',
    value: 'a',
    searchString: 'Option A',
  });
  const [multiSelectValues, setMultiSelectValues] = useState<string[]>(['a']);

  const [labelSelectValue, setLabelSelectValue] = useState<Option | null>({
    label: 'Option A',
    value: 'a',
    searchString: 'Option A',
  });

  const [creatableSelectOptions, setCreatableSelectOptions] = useState<Option[]>([
    { label: 'Option A', value: 'a', searchString: 'Option A' },
    { label: 'Option B', value: 'b', searchString: 'Option B' },
    { label: 'Option C', value: 'c', searchString: 'Option C' },
  ]);
  const [creatableSelectValue, setCreatableSelectValue] = useState<Option | null>(null);

  const [creatableMultiOptions, setCreatableMultiOptions] = useState<Option[]>([
    { label: 'Option A', value: 'a', searchString: 'Option A' },
    { label: 'Option B', value: 'b', searchString: 'Option B' },
    { label: 'Option C', value: 'c', searchString: 'Option C' },
  ]);
  const [creatableMultiValues, setCreatableMultiValues] = useState<string[]>([]);

  // Interaction State
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [searchText, setSearchText] = useState('');

  // TrackingList & FloatingToolbar Interaction
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const toggleItemSelection = (id: string) => {
    setSelectedItems((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  // Draggable Table Data
  const [draggableData, setDraggableData] = useState([
    { id: '1', cols: ['Item A', '100'] },
    { id: '2', cols: ['Item B', '200'] },
    { id: '3', cols: ['Item C', '300'] },
  ]);

  // Realistic Table Data
  type TableDataType = {
    name: string;
    role: string;
    status: string;
    lastLogin: string;
  };

  const columns: ColumnDef<TableDataType>[] = [
    {
      accessorKey: 'name',
      header: 'User Name',
      cell: (info) => <span className='font-weight-bold'>{info.getValue() as string}</span>,
    },
    {
      accessorKey: 'role',
      header: 'Role',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => (
        <StatusBadge
          config={{
            color: info.getValue() === 'Active' ? 'success' : 'secondary',
            label: info.getValue() as string,
            icon: info.getValue() === 'Active' ? 'fa-check-circle' : 'fa-clock',
          }}
        />
      ),
    },
    {
      accessorKey: 'lastLogin',
      header: 'Last Login',
    },
  ];

  const tableData: TableDataType[] = [
    { name: 'Alice Anderson', role: 'Admin', status: 'Active', lastLogin: '2 mins ago' },
    { name: 'Bob Brown', role: 'Editor', status: 'Pending', lastLogin: '1 day ago' },
    { name: 'Charlie Clark', role: 'Viewer', status: 'Inactive', lastLogin: '1 week ago' },
    { name: 'David Davis', role: 'Viewer', status: 'Active', lastLogin: 'Just now' },
    { name: 'Eve Evans', role: 'Editor', status: 'Active', lastLogin: '3 hours ago' },
  ];

  // Badges Data (Truncation)
  const badges = Array.from({ length: 12 }).map((_, i) => (
    <StatusBadge
      key={i}
      config={{
        color: ['success', 'warning', 'danger', 'info', 'primary', 'secondary'][i % 6],
        label: `Badge ${i + 1}`,
        icon: 'fa-tag',
      }}
    />
  ));

  const trackingListItems = [
    { id: '1', title: 'Q1: Scope 1 Emissions', type: 'Number', updated: 'Today' },
    { id: '2', title: 'Q2: Employee Count', type: 'Number', updated: 'Yesterday' },
    { id: '3', title: 'Q3: Policy Document', type: 'Document', updated: 'Last Week' },
  ];

  return (
    <>
      <ShowcaseItem title='Alerts'>
        <BasicAlert type='success' className='w-100'>
          Success Alert
        </BasicAlert>
        <BasicAlert type='info' className='w-100'>
          Info Alert
        </BasicAlert>
        <BasicAlert type='warning' className='w-100'>
          Warning Alert
        </BasicAlert>
        <BasicAlert type='danger' className='w-100'>
          Danger Alert
        </BasicAlert>
        <Button onClick={() => setShowBannerAlert(true)}>Show BannerAlert</Button>
        {showBannerAlert && (
          <BannerAlert handleClose={() => setShowBannerAlert(false)}>
            Banner Alert (Info)
          </BannerAlert>
        )}
      </ShowcaseItem>

      <ShowcaseItem title='AIDisclaimer'>
        <AIDisclaimer />
      </ShowcaseItem>

      <ShowcaseItem title='StatusBadge'>
        <StatusBadge config={{ color: 'success', icon: 'fa-check', label: 'Success' }} />
        <StatusBadge config={{ color: 'warning', icon: 'fa-exclamation-triangle', label: 'Warning' }} />
        <StatusBadge config={{ color: 'danger', icon: 'fa-times-circle', label: 'Danger' }} />
        <StatusBadge config={{ color: 'info', icon: 'fa-info-circle', label: 'Info' }} />
      </ShowcaseItem>

      <ShowcaseItem title='Breadcrumbs'>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Theme Showcase', url: '/theme-showcase' },
            { label: 'Molecules', url: '/theme-showcase/molecules' },
            { label: 'Current Page', url: '/theme-showcase/molecules/current' },
          ]}
          rootLabel='Home'
          rootUrl='/'
        />
      </ShowcaseItem>

      <ShowcaseItem title='Buttons (Molecules)'>
        <IconButton icon='fa-edit' onClick={() => {}}>
          Edit
        </IconButton>
        <SubmitButton>Submit</SubmitButton>
        <ButtonWithLoader
          loading={isLoadingButton}
          onClick={() => {
            setIsLoadingButton(true);
            setTimeout(() => setIsLoadingButton(false), 2000);
          }}
        >
          Click to Load (2s)
        </ButtonWithLoader>
      </ShowcaseItem>

      <ShowcaseItem title='CollapsePanel'>
        <CollapsePanel>
          <CollapseButton>
            <Button color='link'>Expand Me</Button>
          </CollapseButton>
          <CollapseContent>
            <div>Content inside the collapse panel.</div>
          </CollapseContent>
        </CollapsePanel>
      </ShowcaseItem>

      <ShowcaseItem title='ConfirmationModal'>
        <Button onClick={() => setModalOpen(true)}>Open Modal</Button>
        <ConfirmationModal
          isOpen={modalOpen}
          handleCancel={() => setModalOpen(false)}
          title='Confirm Action'
          content='Are you sure you want to proceed?'
          confirmButton={
            <Button color='primary' onClick={() => setModalOpen(false)}>
              Yes, do it
            </Button>
          }
        />
      </ShowcaseItem>

      <ShowcaseItem title='DatePicker'>
        <DatePicker date={datePickerDate} callbackChange={setDatePickerDate} />
      </ShowcaseItem>

      <ShowcaseItem title='Download Components'>
        <div className='d-flex gap-2 align-items-center w-100'>
          <DownloadButton onClick={async () => new Promise((resolve) => setTimeout(resolve, 1000))}>
            Download Report
          </DownloadButton>
          <DownloadButton outline color='primary' onClick={async () => {}}>
            Outline Download
          </DownloadButton>
        </div>

        <h6 className='text-ThemeTextLight mt-3 w-100'>DownloadCard</h6>
        <div className='d-flex gap-3 w-100'>
          <DownloadCard
            item={{ code: 'doc1', name: 'Annual Report 2023', icon: 'fa-file-pdf' }}
            footer={
              <Button size='sm' color='light' block>
                View Details
              </Button>
            }
          />
          <DownloadCard
            item={{ code: 'doc2', name: 'Sustainability Data', icon: 'fa-file-excel' }}
            aside
            disabledText='Requires Premium Access'
          />
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='Feature Components'>
        <div className='d-flex align-items-center w-100'>
          <span className='mr-2'>Intro:</span>
          <FeatureIntro icon='fa-star' header='Sample Feature' content='Description of the feature.' />
        </div>
        <div className='d-flex align-items-center w-100'>
          <span className='mr-2'>Stability:</span>
          <FeatureStability stability='beta' />
          <FeatureStability stability='internal' />

          <span className='mx-2'>Feature:</span>
          <FeatureStability feature={ExtraFeature.StaffReports} />
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='Dashboard Layout'>
        <Dashboard>
          <DashboardSection title='Section Title' subtitle='Section Subtitle'>
            <DashboardRow title='Row Title' subtitle='Row Subtitle'>
              <DashboardColumn title='Column 1'>
                <div className='p-2 border'>Content 1</div>
              </DashboardColumn>
              <DashboardColumn title='Column 2'>
                <div className='p-2 border'>Content 2</div>
              </DashboardColumn>
            </DashboardRow>
            <DashboardRow>
              <DashboardColumn title='Full Width' flexBasisPc='100%'>
                <div className='p-2 border'>Full width content</div>
              </DashboardColumn>
            </DashboardRow>
          </DashboardSection>
        </Dashboard>
      </ShowcaseItem>

      <ShowcaseItem title='Form Controls'>
        <div className='d-flex flex-column gap-2'>
          <FilterToggle label='Filter Toggle' toggleFilters={() => setToggleOn(!toggleOn)} />

          <CheckboxInput
            groupCode='chk-group'
            options={[
              { code: 'chk1', name: 'Checkbox Option 1', instructions: 'Description for option 1' },
              { code: 'chk2', name: 'Checkbox Option 2', instructions: 'Description for option 2' },
            ]}
            form={checkboxState}
            onChange={(e) => setCheckboxState((prev) => ({ ...prev, [e.code]: e.value }))}
          />

          <RadioInput
            groupCode='rad-group'
            value={radioValue}
            options={[
              { code: '1', name: 'Radio Option 1' },
              { code: '2', name: 'Radio Option 2' },
            ]}
            onChange={(e) => setRadioValue(e.value)}
          />

          <ColourPicker handleChange={() => {}} />

          <SwitchGroup
            filterKey='switch-key'
            label='Switch Group (Toggle ME)'
            checked={switchGroupState}
            handleToggle={() => () => setSwitchGroupState(!switchGroupState)}
          />

          <Toggle
            label={`Simple Toggle is ${toggleOn ? 'ON' : 'OFF'}`}
            checked={toggleOn}
            onChange={() => setToggleOn(!toggleOn)}
          />
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='Icons & Indicators'>
        <InfoIcon text='This is an info icon tooltip' />
        <StaffIcon />
        <WorkInProgress title='Component in progress' />
        <span>Selected Count: </span>
        <SelectedCountLabel count={5} />
      </ShowcaseItem>

      <ShowcaseItem title='Lightbox'>
        <Button onClick={() => setLightboxIndex(0)}>Open Lightbox</Button>
        <G17Lightbox
          slides={lightboxSlides}
          photoIndex={lightboxIndex}
          handleReset={() => setLightboxIndex(CLOSED_PHOTO_INDEX)}
        />
      </ShowcaseItem>

      <ShowcaseItem title='LoadingPlaceholder'>
        <LoadingPlaceholder isLoading height={100} />
      </ShowcaseItem>

      <ShowcaseItem title='Menu'>
        <Menu
          items={[
            { label: 'Dashboard', onClick: () => {}, active: true },
            { label: 'Reports', onClick: () => {} },
            { label: 'Settings', onClick: () => {} },
          ]}
        />
      </ShowcaseItem>
      <ShowcaseItem title='Menu (Grouped)'>
        <Menu
          grouped
          items={[
            { label: 'Grouped 1', onClick: () => {} },
            { label: 'Grouped 2', onClick: () => {}, active: true },
          ]}
        />
      </ShowcaseItem>

      <ShowcaseItem title='NoData'>
        <NoData text='No data available demo' />
      </ShowcaseItem>

      <ShowcaseItem title='ExpandableParagraph'>
        <ExpandableParagraph defaultHeight={55}>
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis ut porta ante, efficitur aliquet odio. Sed
          commodo massa ac ante gravida vulputate. Nam vehicula nunc neque, quis vestibulum dolor sagittis ut. Sed
          hendrerit lectus ut urna consequat, in malesuada metus vestibulum. Nulla eget ex blandit, sodales arcu ut,
          volutpat nulla. Etiam mollis felis eget vestibulum fermentum. Nulla dignissim rhoncus est at vehicula.
          Interdum et malesuada fames ac ante ipsum primis in faucibus. Pellentesque ultricies, nisl non viverra
          dapibus, lorem nibh porttitor justo, id interdum risus purus sed magna. Lorem ipsum dolor sit amet,
          consectetur adipiscing elit. Sed fermentum sagittis ipsum at iaculis. Nulla bibendum ullamcorper lacus non
          egestas. Vestibulum magna nulla, blandit sed nulla ut, tempor mollis ipsum. Vivamus nunc felis, pellentesque
          in euismod eu, vestibulum ut arcu. Vestibulum commodo purus vitae interdum tincidunt. Pellentesque dictum
          nulla vel ex feugiat volutpat. Etiam et velit nec ipsum bibendum vulputate id quis urna. Nulla facilisi. Morbi
          dignissim suscipit metus vitae vestibulum. Morbi facilisis at arcu vel gravida. Phasellus eu ante mattis,
          sollicitudin orci nec, dignissim lacus. Suspendisse condimentum luctus erat eget efficitur. Integer sapien
          felis, egestas at augue id, rhoncus lobortis orci. Donec est dui, scelerisque id nisl id, laoreet viverra
          nibh. Nulla scelerisque molestie metus, ut sagittis ligula accumsan ac. Duis dolor lorem, commodo non lobortis
          nec, venenatis sed diam. Nulla rutrum non ipsum quis faucibus. Phasellus ac elit vitae lacus interdum cursus.
          Praesent a massa turpis. Duis tincidunt augue vel est fringilla, sed imperdiet sapien elementum. Morbi
          sagittis eleifend blandit. Morbi diam ipsum, suscipit ut massa pulvinar, consectetur molestie est. In
          condimentum, mauris vel malesuada vulputate, magna mauris laoreet massa, eu maximus risus nulla quis ligula.
          Ut posuere enim neque, nec commodo odio semper sed. Praesent ornare mi et porta posuere. Suspendisse potenti.
          Aliquam vel lectus neque. In varius felis.
        </ExpandableParagraph>
      </ShowcaseItem>

      <ShowcaseItem title='PermissionDenied'>
        <PermissionDenied />
      </ShowcaseItem>

      <ShowcaseItem title='PopoverButton'>
        <PopoverButton title='Popover Title' id='pop-btn'>
          Popover Content
        </PopoverButton>
      </ShowcaseItem>

      <ShowcaseItem title='RadioGroup'>
        <RadioGroup
          groupCode='radio-group-demo'
          options={[
            { code: '1', name: 'Option 1' },
            { code: '2', name: 'Option 2' },
          ]}
          value={radioValue}
          onChange={(e) => setRadioValue(e.target.value)}
        />
      </ShowcaseItem>

      <ShowcaseItem title='Query Components'>
        <div className='w-100'>
          <h6 className='text-ThemeTextLight'>QueryError:</h6>
          <QueryError error={{ status: 500, data: { message: 'Internal Server Error' } }} />
        </div>
        <div className='w-100'>
          <h6 className='text-ThemeTextLight'>QueryWrapper (Success):</h6>
          <QueryWrapper
            query={{
              isLoading: false,
              isFetching: false,
              isUninitialized: false,
              isError: false,
              data: { message: 'Data Loaded Successfully' },
            }}
            onSuccess={(data) => <BasicAlert type='success'>{data.message}</BasicAlert>}
          />
        </div>
        <div className='w-100'>
          <h6 className='text-ThemeTextLight'>QueryWrapper (Loading):</h6>
          <div style={{ position: 'relative', height: '100px' }}>
            <QueryWrapper
              query={{
                isLoading: true,
                isFetching: true,
                isUninitialized: false,
                isError: false,
              }}
              onSuccess={() => null}
            />
          </div>
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='Search'>
        <SearchBox
          placeholder='Instant SearchBox...'
          handleOnChange={(e) => setSearchText(e.target.value)}
          searchText={searchText}
        />
        <DebouncedSearchBox
          placeholder='DebouncedSearchBox (Type to see effect)...'
          onTextChange={(val) => setSearchText(val)}
        />
        <small className='text-ThemeTextLight w-100'>
          Searching for: <strong>{searchText || '(none)'}</strong>
        </small>
      </ShowcaseItem>

      <ShowcaseItem title='SDG Icons'>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map((code) => (
          <SDGIcon key={code} code={String(code)} width={50} height={50} />
        ))}
      </ShowcaseItem>

      <ShowcaseItem title='Workgroup Icon'>
        <WorkgroupIcon workgroup={{ icon: 'user', color: styleVariables.IconSecondary }} />
        <WorkgroupIcon workgroup={{ icon: 'user-pen', color: styleVariables.IconDark }} />
        <WorkgroupIcon workgroup={{ icon: 'user-check', color: styleVariables.IconPrimary }} />
        <WorkgroupIcon workgroup={{ icon: 'user-gear', color: styleVariables.SuccessMedium }} />
        <WorkgroupIcon workgroup={{ icon: 'users', color: styleVariables.DangerMedium }} />
        <WorkgroupIcon workgroup={{ icon: 'user-tie', color: styleVariables.WarningMedium }} />
      </ShowcaseItem>

      <ShowcaseItem title='Selects'>
        <div className='d-flex flex-column gap-3 w-100'>
          <div>
            <h6 className='text-ThemeTextLight mb-2'>SingleSelect</h6>
            <SelectFactory
              selectType={SelectTypes.SingleSelect}
              options={[
                { label: 'Option A', value: 'a', searchString: 'Option A' },
                { label: 'Option B', value: 'b', searchString: 'Option B' },
                { label: 'Option C', value: 'c', searchString: 'Option C' },
              ]}
              value={singleSelectValue}
              onChange={(val) => setSingleSelectValue(val as any)}
            />
          </div>
          <div>
            <h6 className='text-ThemeTextLight mb-2'>MultipleSelect</h6>
            <SelectFactory
              selectType={SelectTypes.MultipleSelect}
              options={[
                { label: 'Option A', value: 'a', searchString: 'Option A' },
                { label: 'Option B', value: 'b', searchString: 'Option B' },
                { label: 'Option C', value: 'c', searchString: 'Option C' },
              ]}
              values={multiSelectValues}
              onChange={(vals) => setMultiSelectValues(vals)}
            />
          </div>
          <div>
            <h6 className='text-ThemeTextLight mb-2'>LabelSelect</h6>
            <SelectFactory
              selectType={SelectTypes.LabelSelect}
              labelProps={{ for: 'label-select', title: 'Label Select Title', className: 'mb-1' }}
              description='This is a LabelSelect description.'
              options={[
                { label: 'Option A', value: 'a', searchString: 'Option A' },
                { label: 'Option B', value: 'b', searchString: 'Option B' },
                { label: 'Option C', value: 'c', searchString: 'Option C' },
              ]}
              value={labelSelectValue}
              onChange={(val) => setLabelSelectValue(val as any)}
            />
          </div>
          <div>
            <h6 className='text-ThemeTextLight mb-2'>CreatableSelect</h6>
            <SelectFactory
              selectType={SelectTypes.CreatableSelect}
              options={creatableSelectOptions}
              value={creatableSelectValue}
              onChange={(val, actionMeta: any) => {
                if (val && actionMeta?.action === 'create-option') {
                  setCreatableSelectOptions((prev) => [...prev, val as Option]);
                }
                setCreatableSelectValue(val as Option);
              }}
            />
          </div>
          <div>
            <h6 className='text-ThemeTextLight mb-2'>CreatableMultipleSelect</h6>
            <SelectFactory
              selectType={SelectTypes.CreatableMultipleSelect}
              options={creatableMultiOptions}
              values={creatableMultiValues}
              onChange={(vals: any, actionMeta: any) => {
                const values = vals as string[];
                if (actionMeta?.action === 'create-option') {
                  const newVals = values.filter((v) => !creatableMultiOptions.find((o) => o.value === v));
                  setCreatableMultiOptions((prev) => [
                    ...prev,
                    ...newVals.map((v) => ({ label: v, value: v, searchString: v })),
                  ]);
                }
                setCreatableMultiValues(values);
              }}
              showSelectedOptions
            />
          </div>
          <small className='text-ThemeTextLight w-100 mt-2'>
            Selected Single: <strong>{singleSelectValue?.label}</strong> | Selected Multi:{' '}
            <strong>{multiSelectValues.join(', ')}</strong> | Selected Label: <strong>{labelSelectValue?.label}</strong>{' '}
            | Selected Creatable: <strong>{creatableSelectValue?.label}</strong> | Selected Creatable Multi:{' '}
            <strong>{creatableMultiValues.join(', ')}</strong>
          </small>
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='Sidebar'>
        <Button onClick={() => setSidebarOpen(true)}>Open Sidebar</Button>
        <Sidebar header='Demo Sidebar' isOpen={sidebarOpen} toggle={() => setSidebarOpen(false)} resizable>
          <div className='p-3'>
            <h5>Sidebar Content</h5>
            <p>This sidebar is resizable and contains custom content.</p>

            <h6 className='mt-4'>Settings Sidebar Demo (Nested)</h6>
            <SettingsSidebar className='border mt-2'>
              <SettingsSection title='Account Settings' icon='fa-user'>
                <div className='p-2'>User Preferences form...</div>
              </SettingsSection>
              <SettingsSection title='Notifications' icon='fa-bell' subtitle='Manage alerts'>
                <div className='p-2'>Notification settings...</div>
              </SettingsSection>
              <div className='p-2'>
                <SettingsSearchBox value='' handleOnChange={() => {}} placeholder='Search settings...' />
              </div>
            </SettingsSidebar>
          </div>
        </Sidebar>
      </ShowcaseItem>

      <ShowcaseItem title='SimpleTooltip'>
        <SimpleTooltip text='I am a tooltip'>
          <span style={{ textDecoration: 'underline' }}>Hover me</span>
        </SimpleTooltip>
      </ShowcaseItem>

      <ShowcaseItem title='TableDraggableRows'>
        <TableDraggableRows
          columns={[
            { header: 'Name', className: 'text-left' },
            { header: 'Value', className: 'text-right' },
          ]}
          data={draggableData}
          handleUpdate={(newData) => {
            const reordered = newData.map((id) => draggableData.find((d) => d.id === id)!);
            setDraggableData(reordered);
          }}
        />
      </ShowcaseItem>

      <ShowcaseItem title='Toasts'>
        <Button
          color='success'
          onClick={() => generateToast({ color: 'success', message: 'Operation completed successfully' })}
        >
          Success Toast
        </Button>
        <Button color='danger' onClick={() => generateToast({ color: 'danger', message: 'Something went wrong' })}>
          Error Toast
        </Button>
        <Button
          color='warning'
          onClick={() => generateToast({ color: 'warning', message: 'Please review your input' })}
        >
          Warning Toast
        </Button>
        <Button color='info' onClick={() => generateToast({ color: 'info', message: 'New data is available' })}>
          Info Toast
        </Button>
      </ShowcaseItem>

      <ShowcaseItem title='BadgesTruncate'>
        <div className='w-100'>
          <BadgesTruncate badges={badges} />
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='Table'>
        <Table data={tableData} columns={columns} />
      </ShowcaseItem>

      <ShowcaseItem title='TimeRangeSelector'>
        <div style={{ width: '300px' }}>
          <p className='small text-ThemeTextLight mb-2'>Select Time Range:</p>
          <TimeRangeSelector
            dateRange={dateRange}
            timeRange={timeRange}
            onChangeDateRange={(dr, period) => {
              setDateRange(dr);
              setTimeRange(period);
            }}
          />
        </div>
      </ShowcaseItem>

      <ShowcaseItem title='TrackingList & FloatingToolbar'>
        <p className='small text-ThemeTextLight mb-2'>
          Select items using the checkboxes to trigger the Floating Toolbar.
        </p>

        <TrackingList border>
          <TrackingListRow header>
            <TrackingListCol className='flex-grow-0' style={{ width: '40px' }} />
            <TrackingListCol stretch>
              <strong>Title</strong>
            </TrackingListCol>
            <TrackingListCol style={{ width: '100px' }}>
              <strong>Type</strong>
            </TrackingListCol>
            <TrackingListCol style={{ width: '120px' }}>
              <strong>Last Updated</strong>
            </TrackingListCol>
            <TrackingListCol style={{ width: '50px' }} />
          </TrackingListRow>

          {trackingListItems.map((item) => (
            <TrackingListRow key={item.id}>
              <TrackingListCol className='flex-grow-0' style={{ width: '40px' }}>
                <CheckboxInput
                  groupCode='list-select'
                  options={[{ code: item.id, name: '' }]}
                  form={{ [item.id]: selectedItems.includes(item.id) }}
                  onChange={() => toggleItemSelection(item.id)}
                />
              </TrackingListCol>
              <TrackingListCol stretch>
                <span className='text-primary'>{item.title}</span>
              </TrackingListCol>
              <TrackingListCol style={{ width: '100px' }}>{item.type}</TrackingListCol>
              <TrackingListCol style={{ width: '120px' }} className='text-ThemeTextLight small'>
                {item.updated}
              </TrackingListCol>
              <TrackingListCol style={{ width: '50px' }}>
                <Button size='sm' color='link' className='p-0'>
                  <i className='fa fa-ellipsis-v text-ThemeTextLight' />
                </Button>
              </TrackingListCol>
            </TrackingListRow>
          ))}
        </TrackingList>

        <FloatingToolbar
          isOpen={selectedItems.length > 0}
          handleClose={() => setSelectedItems([])}
          items={[
            <span key='count' className='text-ThemeTextLight font-weight-bold mr-2'>
              {selectedItems.length} Selected
            </span>,
            <Button key='approve' size='sm' color='success'>
              <i className='fa fa-check mr-1' /> Approve
            </Button>,
            <Button key='reject' size='sm' color='danger'>
              <i className='fa fa-times mr-1' /> Reject
            </Button>,
            <Button key='archive' size='sm' color='secondary'>
              <i className='fa fa-archive mr-1' /> Archive
            </Button>,
          ]}
        />
      </ShowcaseItem>

      <ShowcaseItem title='Footer' className='w-100'>
        <Footer showDisclaimer />
      </ShowcaseItem>
    </>
  );
};
